import React, { useState, useMemo } from 'react'
import type { RefereeTeamTendency } from '../types'
import { useRefereeDetail, RefereeScopeKey } from '../swr-config'
import { useLeague } from '../context/LeagueContext'
import { IoArrowBack, IoRefresh, IoCaretUp, IoCaretDown } from 'react-icons/io5'

interface RefereeDetailPageProps {
  refereeId: number
  onBack: () => void
}

const SCOPE_LABELS: Record<RefereeScopeKey, string> = {
  season: 'Season',
  last10: 'Last 10',
  last5: 'Last 5',
}

// Column definitions with tooltips
type SortKey = 'team' | 'matches' | 'total_fouls' | 'fouls_per_90' | 'fouls_deviation' | 'total_yellows' | 'yellows_per_90' | 'yellows_deviation'

interface ColumnDef {
  key: SortKey
  label: string
  tooltip: string
}

const COLUMNS: ColumnDef[] = [
  { key: 'team', label: 'Team', tooltip: 'Team name' },
  { key: 'matches', label: 'Matches', tooltip: 'Number of matches this referee has officiated for this team' },
  { key: 'total_fouls', label: 'Fouls', tooltip: 'Total fouls committed by this team under this referee' },
  { key: 'fouls_per_90', label: 'Fouls/G', tooltip: 'Average fouls per game under this referee' },
  { key: 'fouls_deviation', label: 'Dev', tooltip: 'Difference from team\'s season average fouls/game. Positive = more fouls under this ref' },
  { key: 'total_yellows', label: 'Yellows', tooltip: 'Total yellow cards received by this team under this referee' },
  { key: 'yellows_per_90', label: 'Yellows/G', tooltip: 'Average yellow cards per game under this referee' },
  { key: 'yellows_deviation', label: 'Dev', tooltip: 'Difference from team\'s season average yellows/game. Positive = more cards under this ref' },
]

function DeviationBadge({ value, type }: { value: number | null; type: 'fouls' | 'cards' }) {
  if (value === null || value === undefined) return <span className="deviation-na">-</span>
  const isPositive = value > 0
  const absValue = Math.abs(value).toFixed(2)
  const colorClass =
    type === 'cards'
      ? isPositive
        ? 'deviation-high'
        : 'deviation-low'
      : isPositive
      ? 'deviation-neutral'
      : 'deviation-low'

  return (
    <span className={`deviation-badge ${colorClass}`}>
      {isPositive ? '+' : ''}
      {value.toFixed(2)}
    </span>
  )
}

export function RefereeDetailPage({ refereeId, onBack }: RefereeDetailPageProps) {
  const { currentLeague } = useLeague()
  const [scope, setScope] = useState<RefereeScopeKey>('season')
  const [selectedSeason, setSelectedSeason] = useState<number | 'all' | null>(null)
  const [sortKey, setSortKey] = useState<SortKey>('matches')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const { data, error, isLoading, mutate } = useRefereeDetail(
    refereeId,
    currentLeague.api_league_id,
    scope,
    selectedSeason
  )

  // Sort team tendencies
  const sortedTendencies = useMemo(() => {
    if (!data?.team_tendencies) return []

    return [...data.team_tendencies].sort((a, b) => {
      let aVal: number | string | null
      let bVal: number | string | null

      switch (sortKey) {
        case 'team':
          aVal = a.team.name.toLowerCase()
          bVal = b.team.name.toLowerCase()
          break
        case 'matches':
          aVal = a.matches
          bVal = b.matches
          break
        case 'total_fouls':
          aVal = a.total_fouls
          bVal = b.total_fouls
          break
        case 'fouls_per_90':
          aVal = a.fouls_per_90
          bVal = b.fouls_per_90
          break
        case 'fouls_deviation':
          aVal = a.fouls_deviation
          bVal = b.fouls_deviation
          break
        case 'total_yellows':
          aVal = a.total_yellows
          bVal = b.total_yellows
          break
        case 'yellows_per_90':
          aVal = a.yellows_per_90
          bVal = b.yellows_per_90
          break
        case 'yellows_deviation':
          aVal = a.yellows_deviation
          bVal = b.yellows_deviation
          break
        default:
          return 0
      }

      // Handle nulls - push to end
      if (aVal === null && bVal === null) return 0
      if (aVal === null) return 1
      if (bVal === null) return -1

      // Compare
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }

      const numA = aVal as number
      const numB = bVal as number
      return sortOrder === 'asc' ? numA - numB : numB - numA
    })
  }, [data?.team_tendencies, sortKey, sortOrder])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortOrder('desc')
    }
  }

  const getStrictnessBadgeClass = (label: string) => {
    switch (label) {
      case 'strict':
        return 'strictness-strict'
      case 'lenient':
        return 'strictness-lenient'
      default:
        return 'strictness-average'
    }
  }

  if (isLoading && !data) {
    return (
      <div className="referee-detail-page">
        <div className="card">
          <div className="card-inner">
            <button className="back-btn" onClick={onBack}>
              <IoArrowBack size={20} /> Back
            </button>
            <div className="skeleton-block" style={{ height: 200 }} />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="referee-detail-page">
        <div className="card">
          <div className="card-inner">
            <button className="back-btn" onClick={onBack}>
              <IoArrowBack size={20} /> Back
            </button>
            <div className="error-state">
              <p>Error loading referee details</p>
              <button className="chip" onClick={() => mutate()}>
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="referee-detail-page">
      {/* Header Card */}
      <div className="card">
        <div className="card-inner">
          <div className="referee-header">
            <button className="back-btn" onClick={onBack}>
              <IoArrowBack size={20} /> Back
            </button>
            <div className="referee-header-content">
              <div className="referee-title">
                <h1>{data.name}</h1>
                {data.country && <span className="country">{data.country}</span>}
              </div>
              <div className="referee-meta">
                <span className={`strictness-badge ${getStrictnessBadgeClass(data.strictness_label)}`}>
                  {data.strictness_label}
                </span>
                <span className="matches-badge">{data.stats.matches} matches</span>
              </div>
            </div>
            <button className="refresh-btn" onClick={() => mutate()} disabled={isLoading}>
              <IoRefresh size={18} className={isLoading ? 'spinning' : ''} />
            </button>
          </div>

          {/* Scope toggle */}
          <div className="row" style={{ gap: 8, marginTop: 16 }}>
            {(['season', 'last10', 'last5'] as RefereeScopeKey[]).map((s) => (
              <button
                key={s}
                className={`chip ${scope === s ? 'chip-active' : ''}`}
                onClick={() => setScope(s)}
              >
                {SCOPE_LABELS[s]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Overall Stats Card */}
      <div className="card">
        <div className="card-inner">
          <h2>Overall Statistics</h2>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-value">{data.stats.yellows_per_90.toFixed(2)}</div>
              <div className="stat-label">Yellows / Game</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{data.stats.reds_per_90.toFixed(2)}</div>
              <div className="stat-label">Reds / Game</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{data.stats.fouls_per_90.toFixed(1)}</div>
              <div className="stat-label">Fouls / Game</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{data.stats.fouls_per_card.toFixed(1)}</div>
              <div className="stat-label">Fouls / Card</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{data.stats.penalties_per_90.toFixed(2)}</div>
              <div className="stat-label">Penalties / Game</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">
                {data.stats.cards_first_half_pct !== null ? `${data.stats.cards_first_half_pct}%` : '-'}
              </div>
              <div className="stat-label">Cards 1st Half</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">
                {data.stats.cards_last_15_min_pct !== null
                  ? `${data.stats.cards_last_15_min_pct}%`
                  : '-'}
              </div>
              <div className="stat-label">Cards Last 15 min</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{(data.stats.second_yellow_rate * 100).toFixed(1)}%</div>
              <div className="stat-label">2nd Yellow Rate</div>
            </div>
          </div>

          {/* Bias section */}
          <div className="bias-section">
            <h3>Bias Indicators</h3>
            <div className="bias-grid">
              <div className="bias-item">
                <span className="bias-label">Home Foul Ratio</span>
                <span className="bias-value">{data.stats.home_foul_ratio.toFixed(2)}</span>
              </div>
              <div className="bias-item">
                <span className="bias-label">Home Card Ratio</span>
                <span className="bias-value">{data.stats.home_card_ratio.toFixed(2)}</span>
              </div>
              <div className="bias-item">
                <span className="bias-label">vs League Avg (Yellows)</span>
                <span className="bias-value">{data.stats.yellows_vs_league_avg.toFixed(2)}x</span>
              </div>
            </div>
          </div>

          {/* Derby context if available */}
          {data.stats.derby_matches > 0 && (
            <div className="derby-section">
              <h3>Derby Matches</h3>
              <p>
                {data.stats.derby_matches} derbies officiated
                {data.stats.derby_yellows_per_90 !== null && (
                  <> &bull; {data.stats.derby_yellows_per_90.toFixed(2)} yellows/game in derbies</>
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Team-by-Team Table */}
      <div className="card">
        <div className="card-inner">
          <h2>Team-by-Team Breakdown</h2>
          <p className="section-subtitle">
            How each team performs under this referee vs their season average
          </p>

          {/* Season filter pills */}
          {data.available_seasons.length > 1 && (
            <div className="row" style={{ gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <button
                className={`chip ${selectedSeason === 'all' ? 'chip-active' : ''}`}
                onClick={() => setSelectedSeason('all')}
              >
                All Seasons
              </button>
              {data.available_seasons.map((season) => (
                <button
                  key={season.year}
                  className={`chip ${selectedSeason === season.year || (selectedSeason === null && data.current_season_year === season.year) ? 'chip-active' : ''}`}
                  onClick={() => setSelectedSeason(season.year)}
                >
                  {season.label}
                </button>
              ))}
            </div>
          )}

          <div className="team-tendencies-table-wrapper">
            <table className="team-tendencies-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className={`sortable-header ${sortKey === col.key ? 'sorted' : ''}`}
                      onClick={() => handleSort(col.key)}
                      title={col.tooltip}
                    >
                      <span className="header-content">
                        {col.label}
                        {sortKey === col.key && (
                          <span className="sort-icon">
                            {sortOrder === 'asc' ? <IoCaretUp size={12} /> : <IoCaretDown size={12} />}
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedTendencies.map((t) => (
                  <tr key={t.team.id}>
                    <td className="team-cell">
                      {t.team.crest_url && (
                        <img src={t.team.crest_url} alt="" className="team-crest-small" />
                      )}
                      <span>{t.team.name}</span>
                    </td>
                    <td>{t.matches}</td>
                    <td>{t.total_fouls}</td>
                    <td>{t.fouls_per_90?.toFixed(1) ?? '-'}</td>
                    <td>
                      <DeviationBadge value={t.fouls_deviation} type="fouls" />
                    </td>
                    <td>{t.total_yellows}</td>
                    <td>{t.yellows_per_90?.toFixed(2) ?? '-'}</td>
                    <td>
                      <DeviationBadge value={t.yellows_deviation} type="cards" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.team_tendencies.length === 0 && (
            <div className="empty-state">
              <p>No team tendency data available.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
